import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not, notEq, or } from 'ember-truth-helpers';
import {
  getErrorTranslationKey,
  LOGIN_CONTEXT,
  shouldReportToSentry,
  VALIDATION_TRANSLATION_KEYS,
} from 'frontend-payments/utils/error-handler';

import styles from './confirmation-code.module.css';

interface LoginConfirmationCodeSignature {
  Args: {
    email: string;
  };
}

export default class LoginConfirmationCode extends Component<LoginConfirmationCodeSignature> {
  @service declare auth: Services['auth'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare session: Services['session'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked code = '';
  @tracked error = '';
  @tracked isAuthenticating = false;
  @tracked resendCooldownSeconds = 60;

  submitTask = task(async (): Promise<void> => {
    if (!this.isValidCode) {
      this.error = this.intl.t(VALIDATION_TRANSLATION_KEYS.INVALID_CODE);
      return;
    }

    this.error = '';
    this.isAuthenticating = true;

    try {
      this.usageAnalytics.initialiseSessionReplay();
      this.usageAnalytics.identify(this.args.email, true);

      this.usageAnalytics.track('User Logged In', {
        eventTrigger: 'login-confirmation-code',
        userId: this.args.email,
      });

      // Authenticate with ember-simple-auth using our custom authenticator
      // ember-simple-auth will automatically redirect to routeAfterAuthentication
      // (configured as 'authenticated.overview' in config/environment.js)
      await this.session.authenticate(
        'authenticator:backend',
        this.args.email,
        this.code,
      );

      this.router.transitionTo('authenticated.overview');
      // Keep isAuthenticating true - we stay in loading state until the redirect happens
      // and this component is destroyed
    } catch (error) {
      this.isAuthenticating = false;
      const translationKey = getErrorTranslationKey(
        error,
        LOGIN_CONTEXT.CODE_VERIFICATION,
      );
      this.error = this.intl.t(translationKey);

      // Only report errors that are system issues, not user errors
      if (shouldReportToSentry(error)) {
        // Set Sentry tags for easy filtering
        this.errorTracking.setTag('feature', 'authentication');
        this.errorTracking.setTag('login_step', 'code_verification');

        this.errorTracking.setUser({
          email: this.args.email,
        });

        this.errorTracking.captureException(error);
      }
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canResendCode() {
    return this.resendCooldownSeconds === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isValidCode() {
    return this.code.length === 6;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get resendButtonText() {
    if (this.resendCooldownSeconds > 0) {
      return this.intl.t('payments.login.resend_code_timer', {
        seconds: this.resendCooldownSeconds,
      });
    }
    return this.intl.t('payments.login.resend_code');
  }

  constructor(owner: Owner, args: LoginConfirmationCodeSignature['Args']) {
    super(owner, args);

    this.startResendCooldown();
  }

  startResendCooldown(): void {
    this.resendCooldownSeconds = 60;

    const timer = setInterval(() => {
      this.resendCooldownSeconds--;

      if (this.resendCooldownSeconds <= 0) {
        clearInterval(timer);
      }
    }, 1000);
  }

  @action
  async handleSubmit(): Promise<void> {
    await this.submitTask.perform();
  }

  @action
  async resendVerificationCode(): Promise<void> {
    if (!this.canResendCode) {
      return;
    }

    try {
      await this.auth.requestTotpCode(this.args.email);
      this.error = '';
      this.startResendCooldown();

      this.usageAnalytics.identify(this.args.email, true);

      this.usageAnalytics.track('Login Code Resent', {
        eventTrigger: 'login-resend-code',
        userId: this.args.email,
      });
    } catch (error) {
      const translationKey = getErrorTranslationKey(
        error,
        LOGIN_CONTEXT.EMAIL_LOGIN,
      );
      this.error = this.intl.t(translationKey);

      // Only report errors that are system issues, not user errors
      if (shouldReportToSentry(error)) {
        this.errorTracking.setTag('feature', 'authentication');
        this.errorTracking.setTag('login_step', 'email_resend');

        this.errorTracking.setUser({
          email: this.args.email,
        });

        this.errorTracking.captureException(error);
      }
    }
  }

  @action
  setCode(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.code = target.value;
    this.error = '';
  }

  @action
  async submitFormWithEnter(event: KeyboardEvent): Promise<void> {
    if (event.key === 'Enter' && this.isValidCode) {
      await this.handleSubmit();
    }
  }

  <template>
    <div class={{styles.container}}>
      <PrismCard @contentLayout="noPadding">
        <:content>
          <div>
            <h2 class="text-emphasis mb-200">
              {{t "payments.login.verify_your_account"}}
            </h2>
            <p class="text-300--regular" data-test-description>
              {{t "payments.login.digit_code_description" email=@email}}
            </p>
          </div>
          {{#if this.error}}
            <PrismBanner
              @bannerMessage={{this.error}}
              @bannerStyle="critical"
              data-test-error-banner
            />
          {{/if}}
          <div>
            <div class="flex w-full text-left">
              <PrismInput
                @autofocus={{true}}
                @errorMessage={{t "payments.login.confirmation_code_invalid"}}
                @hasErrors={{and (not this.isValidCode) (notEq this.code "")}}
                @label={{t "payments.login.code"}}
                @onInput={{this.setCode}}
                @onKeyDown={{this.submitFormWithEnter}}
                @removePadding={{true}}
                @type="text"
                @value={{this.code}}
                data-test-code-input
              />
            </div>
            <PrismButton
              @isDisabled={{or (not this.isValidCode) this.isAuthenticating}}
              @isLoading={{this.isAuthenticating}}
              @label={{t "payments.login"}}
              @onClick={{this.handleSubmit}}
              class="mt-200 w-full"
              data-test-submit-button
            />
          </div>
          <div class="gap-100 text-center">
            <span class="text-300--regular">
              {{t "payments.login.code_not_received"}}
            </span>
            <a
              class="prism-link prism-link--medium
                {{unless this.canResendCode 'prism-link--disabled'}}
                "
              data-test-resend-link
              href="#"
              {{on "click" this.resendVerificationCode}}
            >
              {{this.resendButtonText}}
            </a>
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
